import { AppButton, AppInput } from 'components/form';
import { AppDialog, AppSectionErrorBoundary, AppSpacer } from 'components/ui';
import { useCallback, useEffect, useState } from 'react';

import { LicenseManagerComponent } from './LicenseManagerComponent';
import { IOrganization } from 'types/models/OrganizationModels';
import { Loader } from '@trackman/web-shared-components';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';

interface IOrganizationFormDialogProps {
  isOpened: boolean;
  onClose: () => void;
  organization: IOrganization | null;
}

export const OrganizationFormDialog = observer(({ isOpened, onClose, organization }: IOrganizationFormDialogProps) => {
  const { t } = useTranslation();
  const { organizationsStore } = useStores();

  const formMode = organization?.id ? 'edit' : 'add';

  const [organizationName, setOrganizationName] = useState<string>('');
  const [organizationShortName, setOrganizationShortName] = useState<string>('');
  const [organizationLicenses, setOrganizationLicenses] = useState<string[]>([]);

  useEffect(() => {
    setOrganizationName(organization?.name || '');
    setOrganizationShortName(organization?.shortName || '');
    setOrganizationLicenses(organization?.licenses || []);
  }, [organization]);

  const handleOrganizationAddedSuccessfully = useCallback(() => {
    onClose();
  }, []);
  
  const handleOrganizationNameChange = useCallback(
    (e) => {
      setOrganizationName(e.target.value);

      if (formMode === 'add') setOrganizationShortName(generateShortName(e.target.value));
    },
    [organizationName, organizationShortName]
  );

  const handleOrganizationShortNameChange = useCallback((e) => {
    setOrganizationShortName(e.target.value);
  }, []);

  const handleOrganizationLicenseChange = useCallback((license: string) => {
    setOrganizationLicenses((prevSelectedLicenses) => {
      if (prevSelectedLicenses.includes(license)) {
        const updatedLicenses = prevSelectedLicenses.filter((l) => l !== license);
        return updatedLicenses;
      } else {
        const updatedLicenses = [...prevSelectedLicenses, license];
        return updatedLicenses;
      }
    });
  }, []);

  const onCancelHandler = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose]);

  const resetForm = useCallback(() => {
    setOrganizationName('');
    setOrganizationShortName('');
  }, []);

  const onSubmitHandler = () => {
    if (!organizationName || !organizationShortName) {
      return;
    }

    if (formMode === 'edit') {
      console.log('🔵 OrganizationFormDialog: Calling updateOrganization');
      organizationsStore.updateOrganization(
        { ...organization, name: organizationName, shortName: organizationShortName, licenses: organizationLicenses } as IOrganization,
        () => {
          resetForm();
          onClose();
        }
      );
      return;
    }

    organizationsStore.addOrganization({ name: organizationName, shortName: organizationShortName, licenses: organizationLicenses }, () => {
      resetForm();
      handleOrganizationAddedSuccessfully();
    });
  };

  const isSubmitButtonDisabled = () => {
    if (!organizationName || !organizationShortName) return true;
    if (organizationsStore.isFormLoading) return true;
    if (
      formMode === 'edit' &&
      !organizationsStore.isSelectedOrganizationChanged({ name: organizationName, shortName: organizationShortName, licenses: organizationLicenses })
    )
      return true;

    return false;
  };

  return (
    <AppDialog
      title={formMode === 'add' ? `${t('addOrganization')}` : `${t('editOrganization', { organizationName: organization?.name || '' })}`}
      isOpened={isOpened}
      onClose={onClose}
    >
      <div className='flex-auto d-flex flex-column justify-content-between'>
        <AppSectionErrorBoundary>
          <div>
            <section className='container'>
              <AppInput
                label={t('organizationName')}
                name={'organizationName'}
                value={organizationName}
                disabled={organizationsStore.isFormLoading}
                onChange={handleOrganizationNameChange}
              />
              <AppSpacer space='15px' />
              <AppInput
                label={t('organizationShortName')}
                name={'organizationShortName'}
                value={organizationShortName}
                disabled={organizationsStore.isFormLoading}
                onChange={handleOrganizationShortNameChange}
              />
              <AppSpacer space='15px' />
              <LicenseManagerComponent
                allLicenses={organizationsStore.licenseTypes}
                organizationLicenses={organizationLicenses}
                onUpdate={handleOrganizationLicenseChange}
                isLoading={organizationsStore.isFormLoading}
              />
            </section>
            <AppSpacer space='20px' />
          </div>
          <footer className='d-flex align-items-center justify-content-between p-3 bg-white position-sticky border-top bottom-0 left-0 w-100 z-index-9'>
            <AppButton variant='secondary' onClick={onCancelHandler} data-test='user-dialog-cancel-button'>
              {t('cancel')}
            </AppButton>
            <div className='d-flex'>
              {organizationsStore.isFormLoading && <Loader />}
              <AppSpacer space='10px' />
              <AppButton
                variant='primary'
                disabled={isSubmitButtonDisabled()}
                onClick={onSubmitHandler}
                data-test='user-dialog-submit-button'
              >
                {t('submit')}
              </AppButton>
            </div>
          </footer>
        </AppSectionErrorBoundary>
      </div>
    </AppDialog>
  );
});

const generateShortName = (name: string) => {
  name = name.replace(/\s/g, '');
  return name.substring(0, 3).toUpperCase();
};